import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ReactPaginate from "react-paginate";
import supabase from "../../supabaseClient";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/RCS-Logo-Blue.png";
import { fetchAllSupabaseRows } from "../../utils/supabasePagination";

dayjs.extend(customParseFormat);

const CorporateAnalytics = () => {
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(dayjs().format("YYYY"));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("MM"));
  const [selectedType, setSelectedType] = useState("ALL");
  const [trendPeriod, setTrendPeriod] = useState(12);
  const [kpiTablePage, setKpiTablePage] = useState(0);
  const [kpiStatusFilter, setKpiStatusFilter] = useState(null);
  const [kpiTypeFilter, setKpiTypeFilter] = useState(null);
  const [kpiTransportFilter, setKpiTransportFilter] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [userDepartment, setUserDepartment] = useState(null);

  const kpiTableRef = useRef(null);

  // Handle KPI status click
  const handleKpiStatusClick = (status, shipmentType, transportMode = null) => {
    if (kpiStatusFilter === status && kpiTypeFilter === shipmentType && kpiTransportFilter === transportMode) {
      setKpiStatusFilter(null);
      setKpiTypeFilter(null);
      setKpiTransportFilter(null);
    } else {
      setKpiStatusFilter(status);
      setKpiTypeFilter(shipmentType);
      setKpiTransportFilter(transportMode);
      setKpiTablePage(0);
      setTimeout(() => {
        kpiTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const openModal = (shipment) => setSelectedShipment(shipment);
  const closeModal = () => setSelectedShipment(null);

  // Status color helper
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "order placed":
        return "bg-blue/10 text-blue border border-blue/30";
      case "in warehouse":
        return "bg-yellow/10 text-yellow border border-yellow/30";
      case "departure at origin":
        return "bg-purple-100 text-purple-700 border border-purple-300";
      case "in transit":
        return "bg-orange-100 text-orange-700 border border-orange-300";
      case "arrival at destination":
        return "bg-green-100 text-green-700 border border-green-300";
      case "custom clearance":
        return "bg-indigo-100 text-indigo-700 border border-indigo-300";
      case "delivered":
      case "turnover":
        return "bg-green-100 text-green-800 border border-green-300";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  // Shipment type color helper
  const getShipmentTypeColor = (type) => {
    switch (type) {
      case "AIR":
        return "bg-gradient-to-r from-blue to-blue/80 text-white";
      case "SEA":
        return "bg-gradient-to-r from-teal-500 to-teal-600 text-white";
      case "LAND":
        return "bg-gradient-to-r from-green-500 to-green-600 text-white";
      case "BROKERAGE":
        return "bg-gradient-to-r from-purple-500 to-purple-600 text-white";
      case "DOMESTIC":
        return "bg-gradient-to-r from-yellow to-yellow/80 text-blue";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600 text-white";
    }
  };

  // Get icon for shipment type
  const getShipmentTypeIcon = (type) => {
    switch (type?.toUpperCase()) {
      case "AIR": return "✈️";
      case "SEA": return "🚢";
      case "LAND": return "🚛";
      case "BROKERAGE": return "📋";
      case "DOMESTIC": return "🏠";
      default: return "📦";
    }
  };

  // Download PDF function
  const downloadShipmentPDF = useCallback(() => {
    if (!selectedShipment) return;

    const pdf = new jsPDF();
    const logoX = 10, logoY = 10, logoWidth = 40, logoHeight = 20;
    pdf.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text("Shipment Report", 105, logoY + 15, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text(`BL #: ${selectedShipment.tracking_no || "—"}`, 105, logoY + 22, {
      align: "center",
    });

    let currentY = logoY + logoHeight + 32;
    const filterValidFields = (data) => data.filter(row => row.details !== null && row.details !== undefined && row.details !== "");

    // Shipper Details
    pdf.setFontSize(16);
    pdf.text("Shipper Details", 10, currentY);
    currentY += 8;

    const shipperDetails = filterValidFields([
      { description: "Shipper Name", details: selectedShipment.shipper_name },
      { description: "Shipper Email", details: selectedShipment.shipper_email },
      { description: "Shipper City", details: selectedShipment.shipper_city },
      { description: "Shipper Country", details: selectedShipment.shipper_country },
      { description: "Shipper Phone", details: selectedShipment.shipper_phone },
    ]);

    pdf.autoTable({
      startY: currentY,
      head: [["Description", "Details"]],
      body: shipperDetails.map(row => [row.description, row.details]),
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
    });

    currentY = pdf.lastAutoTable.finalY + 10;

    // Receiver Details
    pdf.setFontSize(16);
    pdf.text("Receiver Details", 10, currentY);
    currentY += 8;

    const receiverDetails = filterValidFields([
      { description: "Receiver Name", details: selectedShipment.receiver_name },
      { description: "Receiver Email", details: selectedShipment.receiver_email },
      { description: "Receiver City", details: selectedShipment.receiver_city },
      { description: "Receiver Country", details: selectedShipment.receiver_country },
      { description: "Receiver Phone", details: selectedShipment.receiver_phone },
    ]);

    pdf.autoTable({
      startY: currentY,
      head: [["Description", "Details"]],
      body: receiverDetails.map(row => [row.description, row.details]),
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
    });

    currentY = pdf.lastAutoTable.finalY + 10;

    // Shipment Details
    pdf.setFontSize(16);
    pdf.text("Shipment Details", 10, currentY);
    currentY += 8;

    const shipmentDetails = filterValidFields([
      { description: "Job Order", details: selectedShipment.job_order },
      { description: "Type", details: selectedShipment.shipment_type },
      { description: "Status", details: selectedShipment.status },
      { description: "Origin", details: selectedShipment.origin || selectedShipment.shipper_city },
      { description: "Destination", details: selectedShipment.destination || selectedShipment.receiver_city },
    ]);

    pdf.autoTable({
      startY: currentY,
      head: [["Description", "Details"]],
      body: shipmentDetails.map(row => [row.description, row.details]),
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
    });

    pdf.save(`shipment-${selectedShipment.tracking_no}.pdf`);
  }, [selectedShipment]);

  // Calculate KPI status for shipments
  const getKPIStatus = (shipment) => {
    const shipmentType = shipment.shipment_type?.toUpperCase();

    if (shipmentType !== "AIR" && shipmentType !== "SEA" && shipmentType !== "BROKERAGE") {
      return null;
    }

    const arrivalRemark = shipment.remarks?.find(r => r.status === "Arrival at Destination");
    if (!arrivalRemark || !arrivalRemark.aad) return null;

    let arrivalDate = dayjs(arrivalRemark.aad);
    if (!arrivalDate.isValid()) {
      arrivalDate = dayjs(arrivalRemark.aad, ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm", "YYYY-MM-DD HH:mm:ss"]);
    }
    if (!arrivalDate.isValid()) return null;

    let completionDate;
    if (shipment.status === "Delivered" || shipment.status === "Turnover") {
      const completionRemark = shipment.remarks?.find(r => r.status === "Delivered" || r.status === "Turnover");
      completionDate = completionRemark?.date ? dayjs(completionRemark.date) : dayjs();
    } else {
      completionDate = dayjs();
    }

    const daysDiff = completionDate.diff(arrivalDate, "day");

    if (daysDiff < 0) {
      return { status: "On Time", color: "green", bgColor: "bg-green-100", textColor: "text-green-700", days: 0 };
    }

    if (shipmentType === "AIR") {
      if (daysDiff <= 2) return { status: "On Time", color: "green", bgColor: "bg-green-100", textColor: "text-green-700", days: daysDiff };
      return { status: "Delayed", color: "red", bgColor: "bg-red-100", textColor: "text-red-700", days: daysDiff };
    }

    if (shipmentType === "SEA") {
      if (daysDiff <= 5) return { status: "On Time", color: "green", bgColor: "bg-green-100", textColor: "text-green-700", days: daysDiff };
      if (daysDiff <= 10) return { status: "Slightly Delayed", color: "orange", bgColor: "bg-orange-200", textColor: "text-orange-700", days: daysDiff };
      return { status: "Delayed", color: "red", bgColor: "bg-red-100", textColor: "text-red-700", days: daysDiff };
    }

    if (shipmentType === "BROKERAGE") {
      const transportMode = shipment.shipment_transport;
      if (transportMode === "AIR") {
        if (daysDiff <= 2) return { status: "On Time", color: "green", bgColor: "bg-green-100", textColor: "text-green-700", days: daysDiff };
        return { status: "Delayed", color: "red", bgColor: "bg-red-100", textColor: "text-red-700", days: daysDiff };
      } else if (transportMode === "SEA") {
        if (daysDiff <= 5) return { status: "On Time", color: "green", bgColor: "bg-green-100", textColor: "text-green-700", days: daysDiff };
        if (daysDiff <= 10) return { status: "Slightly Delayed", color: "orange", bgColor: "bg-orange-200", textColor: "text-orange-700", days: daysDiff };
        return { status: "Delayed", color: "red", bgColor: "bg-red-100", textColor: "text-red-700", days: daysDiff };
      }
    }

    return null;
  };

  // Fetch shipments filtered by corporate client
  useEffect(() => {
    const fetchShipments = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        // Get user's department
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("department")
          .eq("user_id", user.id)
          .single();

        if (userError || !userData) {
          console.error("Error fetching user department:", userError);
          setLoading(false);
          return;
        }

        const department = userData.department;
        setUserDepartment(department);

        // Fetch shipments filtered by corporate_client
        const { data, error } = await fetchAllSupabaseRows(() =>
          supabase
            .from("shipments")
            .select("*")
            .eq("corporate_client", department)
            .order("date", { ascending: true })
        );

        if (error) throw error;
        setShipments(data || []);
      } catch (error) {
        console.error("Error fetching shipments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();

    // Real-time subscription
    const subscription = supabase
      .channel("corporate-analytics-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        async () => {
          if (userDepartment) {
            const { data, error } = await fetchAllSupabaseRows(() =>
              supabase
                .from("shipments")
                .select("*")
                .eq("corporate_client", userDepartment)
                .order("date", { ascending: true })
            );
            if (!error && data) setShipments(data);
          }
        }
      )
      .subscribe();

    return () => {
      if (subscription) supabase.removeChannel(subscription);
    };
  }, [user?.id, userDepartment]);

  const shipmentTypes = ["ALL", "AIR", "BROKERAGE", "DOMESTIC", "LAND", "SEA"];

  const extractDateFromJobOrder = (jobOrder) => {
    if (!jobOrder || jobOrder.length < 8) return null;
    try {
      const month = parseInt(jobOrder.substring(2, 4));
      const year = parseInt(jobOrder.substring(6, 8));
      if (month < 1 || month > 12) return null;
      return { month, year: 2000 + year };
    } catch {
      return null;
    }
  };

  // Calculate analytics data
  const analytics = useMemo(() => {
    if (!shipments.length) return null;

    let selectedMonthShipments = shipments.filter((row) => {
      const joDate = extractDateFromJobOrder(row.job_order);
      if (!joDate) return false;
      const matchesMonth = selectedMonth === "All" ? true : joDate.month === parseInt(selectedMonth);
      const matchesYear = selectedYear === "All" ? true : joDate.year === parseInt(selectedYear);
      return matchesMonth && matchesYear;
    });

    if (selectedType !== "ALL") {
      selectedMonthShipments = selectedMonthShipments.filter(row => row.shipment_type === selectedType);
    }

    const totalJobOrders = selectedMonthShipments.length;

    const modeBreakdown = selectedMonthShipments.reduce((acc, shipment) => {
      let type = shipment.shipment_type?.trim() || "Not Specified";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const modeData = Object.entries(modeBreakdown)
      .map(([name, value]) => ({
        name,
        value,
        percentage: ((value / totalJobOrders) * 100).toFixed(1),
      }))
      .sort((a, b) => b.value - a.value);

    // Trend data
    let trendData = [];
    if (trendPeriod === "yearly") {
      const yearsWithData = shipments
        .map(s => {
          const joDate = extractDateFromJobOrder(s.job_order);
          if (joDate) return joDate.year;
          if (s.date) return new Date(s.date).getFullYear();
          return null;
        })
        .filter(year => year !== null);

      if (yearsWithData.length > 0) {
        const earliestYear = Math.min(...yearsWithData);
        const currentYear = dayjs().year();
        for (let year = earliestYear; year <= currentYear; year++) {
          const count = shipments.filter(s => {
            const joDate = extractDateFromJobOrder(s.job_order);
            if (joDate) return joDate.year === year;
            if (s.date) return new Date(s.date).getFullYear() === year;
            return false;
          }).length;
          trendData.push({ month: year.toString(), count, fullDate: year.toString() });
        }
      }
    } else {
      const selectedDate = dayjs(`${selectedYear}-${selectedMonth}-01`);
      const monthsToShow = trendPeriod - 1;
      for (let i = monthsToShow; i >= 0; i--) {
        const targetDate = selectedDate.subtract(i, "month");
        const targetMonth = targetDate.month() + 1;
        const targetYear = targetDate.year();
        const monthName = targetDate.format("MMM YYYY");
        const count = shipments.filter(s => {
          const joDate = extractDateFromJobOrder(s.job_order);
          if (joDate) return joDate.month === targetMonth && joDate.year === targetYear;
          if (s.date) return dayjs(s.date).format("YYYY-MM") === targetDate.format("YYYY-MM");
          return false;
        }).length;
        trendData.push({ month: monthName, count, fullDate: targetDate.format("YYYY-MM") });
      }
    }

    // KPI calculations
    const airShipments = selectedMonthShipments.filter(s => s.shipment_type === "AIR");
    const seaShipments = selectedMonthShipments.filter(s => s.shipment_type === "SEA");
    const brokerageShipments = selectedMonthShipments.filter(s => s.shipment_type === "BROKERAGE");

    const airKPI = { total: airShipments.length, onTime: 0, delayed: 0, noArrivalData: 0 };
    const seaKPI = { total: seaShipments.length, onTime: 0, slightlyDelayed: 0, delayed: 0, noArrivalData: 0 };
    const brokerageAirKPI = { total: brokerageShipments.filter(s => s.shipment_transport === "AIR").length, onTime: 0, delayed: 0, noArrivalData: 0 };
    const brokerageSeaKPI = { total: brokerageShipments.filter(s => s.shipment_transport === "SEA").length, onTime: 0, slightlyDelayed: 0, delayed: 0, noArrivalData: 0 };

    airShipments.forEach(shipment => {
      const kpi = getKPIStatus(shipment);
      if (!kpi) { airKPI.noArrivalData++; return; }
      if (kpi.status === "On Time") airKPI.onTime++;
      else if (kpi.status === "Delayed") airKPI.delayed++;
    });

    seaShipments.forEach(shipment => {
      const kpi = getKPIStatus(shipment);
      if (!kpi) { seaKPI.noArrivalData++; return; }
      if (kpi.status === "On Time") seaKPI.onTime++;
      else if (kpi.status === "Slightly Delayed") seaKPI.slightlyDelayed++;
      else if (kpi.status === "Delayed") seaKPI.delayed++;
    });

    brokerageShipments.filter(s => s.shipment_transport === "AIR").forEach(shipment => {
      const kpi = getKPIStatus(shipment);
      if (!kpi) { brokerageAirKPI.noArrivalData++; return; }
      if (kpi.status === "On Time") brokerageAirKPI.onTime++;
      else if (kpi.status === "Delayed") brokerageAirKPI.delayed++;
    });

    brokerageShipments.filter(s => s.shipment_transport === "SEA").forEach(shipment => {
      const kpi = getKPIStatus(shipment);
      if (!kpi) { brokerageSeaKPI.noArrivalData++; return; }
      if (kpi.status === "On Time") brokerageSeaKPI.onTime++;
      else if (kpi.status === "Slightly Delayed") brokerageSeaKPI.slightlyDelayed++;
      else if (kpi.status === "Delayed") brokerageSeaKPI.delayed++;
    });

    // Completed shipments
    const isShipmentCompleted = (shipment) => {
      if (["delivered", "turnover"].includes(shipment.status?.toLowerCase())) return true;
      const isAirExportDoorOrPortToPort = shipment.shipment_type === "AIR" &&
        (shipment.shipment_mode === "EXPORT" || shipment.shipment_mode === "EXPORT-EVENT") &&
        (shipment.delivery_mode === "DOOR-PORT" || shipment.delivery_mode === "PORT-PORT");
      return isAirExportDoorOrPortToPort && shipment.status === "Arrival at Destination";
    };

    const currentMonthCount = selectedMonthShipments.filter(isShipmentCompleted).length;

    const previousMonthDate = dayjs(`${selectedYear}-${selectedMonth}-01`).subtract(1, "month");
    const previousMonthShipments = shipments.filter(row => {
      const joDate = extractDateFromJobOrder(row.job_order);
      if (!joDate) return false;
      return joDate.month === previousMonthDate.month() + 1 && joDate.year === previousMonthDate.year();
    });
    const previousMonthCount = previousMonthShipments.filter(isShipmentCompleted).length;

    const growthPercentage = previousMonthCount > 0
      ? (((currentMonthCount - previousMonthCount) / previousMonthCount) * 100).toFixed(1)
      : 0;

    return {
      totalJobOrders,
      modeData,
      monthlyComparison: trendData,
      growthPercentage,
      currentMonthCount,
      previousMonthCount,
      airKPI,
      seaKPI,
      brokerageAirKPI,
      brokerageSeaKPI,
    };
  }, [shipments, selectedYear, selectedMonth, selectedType, trendPeriod]);

  // Filter shipments for KPI table
  const filteredKpiShipments = useMemo(() => {
    if (!kpiStatusFilter || !kpiTypeFilter) return [];

    return shipments.filter(shipment => {
      // Type filter
      if (kpiTypeFilter === "BROKERAGE") {
        if (shipment.shipment_type !== "BROKERAGE") return false;
        if (kpiTransportFilter && shipment.shipment_transport !== kpiTransportFilter) return false;
      } else if (shipment.shipment_type !== kpiTypeFilter) {
        return false;
      }

      // Status filter
      const kpi = getKPIStatus(shipment);
      if (kpiStatusFilter === "No Arrival Data") return !kpi;
      return kpi?.status === kpiStatusFilter;
    });
  }, [shipments, kpiStatusFilter, kpiTypeFilter, kpiTransportFilter]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-blue/20">
          <p className="text-blue font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-grey text-sm">
              {entry.name}: <span className="font-bold text-blue">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <section className="flex flex-col justify-center font-poppins min-h-screen bg-gray-100">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto">
              <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
            </div>
            <div className="text-blue font-medium text-lg mt-4">Loading Analytics...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col justify-center font-poppins min-h-screen bg-gray-100">
      <div className="flex flex-col p-6 gap-6">
        {/* Header */}
        <div className="text-center mb-2">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-chart-line text-white text-xl"></i>
            </div>
            <h1 className="text-4xl font-bold text-blue">Analytics & Reporting</h1>
          </div>
          <p className="text-grey font-medium">
            {userDepartment ? `${userDepartment} - ` : ""}Performance metrics and insights
          </p>
        </div>

        {/* Active Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {selectedYear !== "All" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <i className="fas fa-calendar-alt text-blue"></i>
              <span className="text-blue font-semibold text-sm">Year: {selectedYear}</span>
            </div>
          )}
          {selectedMonth !== "All" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <i className="fas fa-calendar text-blue"></i>
              <span className="text-blue font-semibold text-sm">
                {dayjs(`2024-${selectedMonth}-01`).format("MMMM")}
              </span>
            </div>
          )}
          {selectedType !== "ALL" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <span className="text-xl">{getShipmentTypeIcon(selectedType)}</span>
              <span className="text-blue font-semibold text-sm">{selectedType}</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <i className="fas fa-filter text-blue text-2xl"></i>
              <h2 className="text-2xl font-bold text-blue">Filters</h2>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-grey">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white text-blue font-semibold"
                  >
                    <option value="All">All Years</option>
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = dayjs().year() - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-grey">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white text-blue font-semibold"
                  >
                    <option value="All">All Months</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, "0");
                      return <option key={month} value={month}>{dayjs(`2024-${month}-01`).format("MMMM")}</option>;
                    })}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-grey">Shipment Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white text-blue font-semibold min-w-[150px]"
                  >
                    {shipmentTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedYear(dayjs().format("YYYY"));
                  setSelectedMonth(dayjs().format("MM"));
                  setSelectedType("ALL");
                  setKpiStatusFilter(null);
                  setKpiTypeFilter(null);
                }}
                className="px-6 py-3 bg-blue hover:bg-blue/80 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                <i className="fas fa-sync-alt mr-2"></i>Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-blue/10 rounded-2xl flex items-center justify-center">
                <i className="fas fa-file-alt text-blue text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">Total Job Orders</h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">{analytics?.totalJobOrders || 0}</p>
            <p className="text-sm text-grey">
              {selectedYear === "All" && selectedMonth === "All" ? "All Time" :
                selectedMonth === "All" ? `All Months ${selectedYear}` :
                  dayjs(`${selectedYear}-${selectedMonth}-01`).format("MMMM YYYY")}
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center">
                <i className="fas fa-chart-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">Growth Rate</h3>
            </div>
            <p className={`text-4xl font-bold mb-2 ${parseFloat(analytics?.growthPercentage) >= 0 ? "text-green-600" : "text-red-500"}`}>
              {analytics?.growthPercentage >= 0 ? "+" : ""}{analytics?.growthPercentage || 0}%
            </p>
            <p className="text-sm text-grey">vs Previous Month</p>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-yellow/10 rounded-2xl flex items-center justify-center">
                <i className="fas fa-calendar-day text-yellow text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">This Month</h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">{analytics?.currentMonthCount || 0}</p>
            <p className="text-sm text-grey">Completed Shipments</p>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
                <i className="fas fa-history text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">Last Month</h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">{analytics?.previousMonthCount || 0}</p>
            <p className="text-sm text-grey">Completed Shipments</p>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
                <i className="fas fa-chart-area text-blue text-xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue">Shipment Trend</h3>
                <p className="text-sm text-grey">Monthly shipment volume</p>
              </div>
            </div>
            <div className="flex gap-2">
              {[3, 6, 12].map(period => (
                <button
                  key={period}
                  onClick={() => setTrendPeriod(period)}
                  className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                    trendPeriod === period ? "bg-blue text-white" : "bg-gray-100 text-grey hover:bg-gray-200"
                  }`}
                >
                  {period}M
                </button>
              ))}
              <button
                onClick={() => setTrendPeriod("yearly")}
                className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                  trendPeriod === "yearly" ? "bg-blue text-white" : "bg-gray-100 text-grey hover:bg-gray-200"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics?.monthlyComparison || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Shipments"
                  stroke="#114878"
                  strokeWidth={3}
                  dot={{ fill: "#114878", strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, fill: "#FDB913" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Type Breakdown */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
              <i className="fas fa-boxes text-blue text-xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-blue">Shipment Type Breakdown</h3>
              <p className="text-sm text-grey">Distribution by shipment type</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-grey font-semibold">Type</th>
                  <th className="text-right py-3 px-4 text-grey font-semibold">Count</th>
                  <th className="text-right py-3 px-4 text-grey font-semibold">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.modeData?.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getShipmentTypeIcon(item.name)}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getShipmentTypeColor(item.name)}`}>
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 font-bold text-blue">{item.value}</td>
                    <td className="text-right py-3 px-4 text-grey">{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Air KPI */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
                <span className="text-3xl">✈️</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue">Air Shipments KPI</h3>
                <p className="text-sm text-grey">Total: {analytics?.airKPI.total || 0} shipments</p>
              </div>
            </div>
            <div className="space-y-4">
              <div
                onClick={() => handleKpiStatusClick("On Time", "AIR")}
                className={`bg-green-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "On Time" && kpiTypeFilter === "AIR" ? "border-green-500 ring-4 ring-green-200" : "border-green-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-semibold text-grey">On Time (1-2 days)</span>
                  </div>
                  <span className="text-2xl font-bold text-green-700">{analytics?.airKPI.onTime || 0}</span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${analytics?.airKPI.total > 0 ? (analytics.airKPI.onTime / analytics.airKPI.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleKpiStatusClick("Delayed", "AIR")}
                className={`bg-red-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "Delayed" && kpiTypeFilter === "AIR" ? "border-red-500 ring-4 ring-red-200" : "border-red-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-semibold text-grey">Delayed (&gt;2 days)</span>
                  </div>
                  <span className="text-2xl font-bold text-red-700">{analytics?.airKPI.delayed || 0}</span>
                </div>
                <div className="w-full bg-red-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all"
                    style={{ width: `${analytics?.airKPI.total > 0 ? (analytics.airKPI.delayed / analytics.airKPI.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleKpiStatusClick("No Arrival Data", "AIR")}
                className={`bg-gray-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "AIR" ? "border-gray-500 ring-4 ring-gray-200" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="font-semibold text-grey">No Arrival Data</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-700">{analytics?.airKPI.noArrivalData || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sea KPI */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center">
                <span className="text-3xl">🚢</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue">Sea Shipments KPI</h3>
                <p className="text-sm text-grey">Total: {analytics?.seaKPI.total || 0} shipments</p>
              </div>
            </div>
            <div className="space-y-4">
              <div
                onClick={() => handleKpiStatusClick("On Time", "SEA")}
                className={`bg-green-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "On Time" && kpiTypeFilter === "SEA" ? "border-green-500 ring-4 ring-green-200" : "border-green-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-semibold text-grey">On Time (1-5 days)</span>
                  </div>
                  <span className="text-2xl font-bold text-green-700">{analytics?.seaKPI.onTime || 0}</span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ width: `${analytics?.seaKPI.total > 0 ? (analytics.seaKPI.onTime / analytics.seaKPI.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleKpiStatusClick("Slightly Delayed", "SEA")}
                className={`bg-orange-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "Slightly Delayed" && kpiTypeFilter === "SEA" ? "border-orange-500 ring-4 ring-orange-200" : "border-orange-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="font-semibold text-grey">Slightly Delayed (6-10 days)</span>
                  </div>
                  <span className="text-2xl font-bold text-orange-700">{analytics?.seaKPI.slightlyDelayed || 0}</span>
                </div>
                <div className="w-full bg-orange-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all"
                    style={{ width: `${analytics?.seaKPI.total > 0 ? (analytics.seaKPI.slightlyDelayed / analytics.seaKPI.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleKpiStatusClick("Delayed", "SEA")}
                className={`bg-red-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "Delayed" && kpiTypeFilter === "SEA" ? "border-red-500 ring-4 ring-red-200" : "border-red-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-semibold text-grey">Delayed (&gt;10 days)</span>
                  </div>
                  <span className="text-2xl font-bold text-red-700">{analytics?.seaKPI.delayed || 0}</span>
                </div>
                <div className="w-full bg-red-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all"
                    style={{ width: `${analytics?.seaKPI.total > 0 ? (analytics.seaKPI.delayed / analytics.seaKPI.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div
                onClick={() => handleKpiStatusClick("No Arrival Data", "SEA")}
                className={`bg-gray-50 border-2 rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${
                  kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "SEA" ? "border-gray-500 ring-4 ring-gray-200" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="font-semibold text-grey">No Arrival Data</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-700">{analytics?.seaKPI.noArrivalData || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Detail Table */}
        {kpiStatusFilter && kpiTypeFilter && filteredKpiShipments.length > 0 && (
          <div ref={kpiTableRef} className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-blue">
                  {kpiTypeFilter} Shipments - {kpiStatusFilter}
                </h3>
                <span className="bg-blue/10 text-blue px-3 py-1 rounded-full text-sm font-semibold">
                  {filteredKpiShipments.length} shipments
                </span>
              </div>
              <button
                onClick={() => {
                  setKpiStatusFilter(null);
                  setKpiTypeFilter(null);
                  setKpiTransportFilter(null);
                }}
                className="px-4 py-2 bg-gray-100 text-grey rounded-xl hover:bg-gray-200 transition-all"
              >
                <i className="fas fa-times mr-2"></i>Clear Filter
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-grey font-semibold">Job Order</th>
                    <th className="text-left py-3 px-4 text-grey font-semibold">Type</th>
                    <th className="text-left py-3 px-4 text-grey font-semibold">Status</th>
                    <th className="text-left py-3 px-4 text-grey font-semibold">KPI Status</th>
                    <th className="text-right py-3 px-4 text-grey font-semibold">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKpiShipments
                    .slice(kpiTablePage * 10, (kpiTablePage + 1) * 10)
                    .map((shipment, index) => {
                      const kpi = getKPIStatus(shipment);
                      return (
                        <tr
                          key={index}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => openModal(shipment)}
                        >
                          <td className="py-3 px-4 font-semibold text-blue">{shipment.job_order}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getShipmentTypeColor(shipment.shipment_type)}`}>
                              {shipment.shipment_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(shipment.status)}`}>
                              {shipment.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${kpi?.bgColor || "bg-gray-100"} ${kpi?.textColor || "text-gray-700"}`}>
                              {kpi?.status || "No Arrival Data"}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4 font-bold text-grey">{kpi?.days ?? "-"}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {filteredKpiShipments.length > 10 && (
              <div className="mt-4 flex justify-center">
                <ReactPaginate
                  pageCount={Math.ceil(filteredKpiShipments.length / 10)}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  onPageChange={({ selected }) => setKpiTablePage(selected)}
                  forcePage={kpiTablePage}
                  containerClassName="flex gap-2"
                  pageClassName="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all"
                  activeClassName="!bg-blue text-white"
                  previousClassName="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200"
                  nextClassName="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200"
                  previousLabel="<"
                  nextLabel=">"
                />
              </div>
            )}
          </div>
        )}

        {/* Shipment Detail Modal */}
        {selectedShipment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-blue">{selectedShipment.job_order}</h2>
                  <p className="text-grey">{selectedShipment.tracking_no}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-all"
                >
                  <i className="fas fa-times text-grey"></i>
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-grey">Status</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(selectedShipment.status)}`}>
                      {selectedShipment.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-grey">Type</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getShipmentTypeColor(selectedShipment.shipment_type)}`}>
                      {selectedShipment.shipment_type}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-grey">Origin</p>
                    <p className="font-semibold text-blue">{selectedShipment.origin || selectedShipment.shipper_city || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-grey">Destination</p>
                    <p className="font-semibold text-blue">{selectedShipment.destination || selectedShipment.receiver_city || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-grey">Shipper</p>
                    <p className="font-semibold text-blue">{selectedShipment.shipper_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-grey">Receiver</p>
                    <p className="font-semibold text-blue">{selectedShipment.receiver_name || "-"}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={downloadShipmentPDF}
                    className="flex-1 px-4 py-3 bg-blue text-white font-semibold rounded-xl hover:bg-blue/90 transition-all"
                  >
                    <i className="fas fa-download mr-2"></i>Download PDF
                  </button>
                  <button
                    onClick={closeModal}
                    className="flex-1 px-4 py-3 bg-gray-100 text-grey font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CorporateAnalytics;
